import {
	getFormProps,
	getInputProps,
	getTextareaProps,
	useForm,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import * as E from '@react-email/components'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { data, Form, Link, useLoaderData } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import {
	ContactConfirmationEmail,
	getContactConfirmationSubject,
} from '#app/components/emails/contact-confirmation.tsx'
import { ErrorList, Field, TextareaField } from '#app/components/forms.tsx'
import { HeroBackdrop } from '#app/components/hero-backdrop.tsx'
import { Alert } from '#app/components/ui/alert.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Card } from '#app/components/ui/card.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { getInstance } from '#app/middleware/i18next.ts'
import { getAdminEmail, sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { DEFAULT_LANGUAGE, useLocalizedPath } from '#app/utils/i18n.ts'
import { logIntegrationCall } from '#app/utils/integration-log.server.tsx'
import { useIsPending } from '#app/utils/misc.tsx'
import {
	generateContactPointJsonLd,
	generateMarketingMeta,
	getLayoutSeoData,
} from '#app/utils/seo.ts'
import { redirectWithToast } from '#app/utils/toast.server.ts'
import { trackEvent } from '#app/utils/tracking.ts'
import { type Route } from './+types/contact.ts'

const ContactSchema = z.object({
	name: z.string().min(1),
	email: z.string().email(),
	subject: z.string().min(1),
	message: z.string().min(10),
})

export const handle = { i18n: 'common' }

/**
 * `?anliegen=` values we prefill the form for, mapped to their subject and
 * message placeholder keys: the free Standortbestimmung from a Coaching CTA
 * and the offer requests from the Nextcloud and Cloudron pages. Any
 * other/absent value falls back to the blank form.
 */
const COACHING_ORIGIN = 'coaching'
const ANLIEGEN = {
	[COACHING_ORIGIN]: {
		subject: 'contact.coachingSubject',
		placeholder: 'contact.coachingMessagePlaceholder',
	},
	nextcloud: {
		subject: 'contact.nextcloudOfferSubject',
		placeholder: 'contact.offerMessagePlaceholder',
	},
	cloudron: {
		subject: 'contact.cloudronOfferSubject',
		placeholder: 'contact.offerMessagePlaceholder',
	},
} as const
type Anliegen = keyof typeof ANLIEGEN

function isAnliegen(value: string | null): value is Anliegen {
	return value !== null && Object.hasOwn(ANLIEGEN, value)
}

export async function loader({ request, params, context }: Route.LoaderArgs) {
	const anliegen = new URL(request.url).searchParams.get('anliegen')
	if (!isAnliegen(anliegen)) {
		return { origin: 'direct' as const, subjectDefault: '' }
	}

	// Resolve the subject via the request-scoped i18n instance (locale-synced
	// by the _layout loader) so the copy always matches the served locale.
	const lang = params.lang ?? DEFAULT_LANGUAGE
	const t = getInstance(context).getFixedT(lang, 'common')
	return {
		origin: anliegen,
		subjectDefault: t(ANLIEGEN[anliegen].subject),
	}
}

export const meta: Route.MetaFunction = ({ matches }) => {
	const { siteUrl, lang } = getLayoutSeoData(matches)
	return [
		...generateMarketingMeta({
			title: 'Kontakt | Open Sourced',
			description:
				'Kontaktieren Sie Open Sourced zu Linux, Datenschutz, Open-Source-Lösungen und Hosting für Organisationen. Wir sind für Sie da.',
			siteUrl,
			lang,
			path: '/contact',
		}),
		{ 'script:ld+json': generateContactPointJsonLd(siteUrl, lang) },
	]
}

export async function action({ request, params }: Route.ActionArgs) {
	const lang = params.lang ?? 'de'
	const formData = await request.formData()
	await checkHoneypot(formData)

	const submission = parseWithZod(formData, { schema: ContactSchema })
	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { name, email, subject, message } = submission.value

	const response = await sendEmail({
		to: getAdminEmail(),
		subject: `Kontaktformular: ${subject}`,
		replyTo: email,
		react: (
			<ContactEmail
				name={name}
				email={email}
				subject={subject}
				message={message}
			/>
		),
	})

	if (response.status === 'success') {
		const locale = lang === 'fr' || lang === 'en' ? lang : 'de'

		// Confirmation email to the visitor, fire-and-forget: a slow or
		// failing send never delays or breaks the redirect.
		void logIntegrationCall({
			integration: 'email',
			operation: 'contactConfirmation',
			direction: 'outbound',
			correlationId: null,
			isFailure: (r) => r.status === 'error',
			fn: () =>
				sendEmail({
					to: email,
					subject: getContactConfirmationSubject(locale),
					react: (
						<ContactConfirmationEmail
							locale={locale}
							name={name}
							subject={subject}
							message={message}
						/>
					),
				}),
		}).catch((error) => {
			console.error('Contact confirmation email failed:', error)
		})

		return redirectWithToast(`/${lang}/contact`, {
			type: 'success',
			description: 'Ihre Nachricht wurde gesendet. Wir melden uns bei Ihnen.',
		})
	}

	return data(
		{
			result: submission.reply({
				formErrors: [
					'Beim Senden der Nachricht ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.',
				],
			}),
		},
		{ status: 500 },
	)
}

function ContactEmail({
	name,
	email,
	subject,
	message,
}: {
	name: string
	email: string
	subject: string
	message: string
}) {
	return (
		<E.Html lang="de" dir="ltr">
			<E.Container>
				<h1>
					<E.Text>Neue Kontaktanfrage</E.Text>
				</h1>
				<E.Text>
					<strong>Name:</strong> {name}
				</E.Text>
				<E.Text>
					<strong>E-Mail:</strong> {email}
				</E.Text>
				<E.Text>
					<strong>Betreff:</strong> {subject}
				</E.Text>
				<E.Text>
					<strong>Nachricht:</strong>
				</E.Text>
				<E.Text>{message}</E.Text>
			</E.Container>
		</E.Html>
	)
}

// The hosting offers for Organisationen, Managed Cloudron first.
const organisationLinks = [
	{ to: '/cloudron', labelKey: 'contact.organisationCloudronLinkLabel' },
	{ to: '/nextcloud', labelKey: 'contact.organisationLinkLabel' },
] as const

export default function ContactRoute({ actionData }: Route.ComponentProps) {
	const isPending = useIsPending()
	const { t } = useTranslation()
	const { subjectDefault, origin } = useLoaderData<typeof loader>()
	const isCoaching = origin === COACHING_ORIGIN
	const formStartedRef = useRef(false)

	const [form, fields] = useForm({
		id: 'contact-form',
		constraint: getZodConstraint(ContactSchema),
		lastResult: actionData?.result,
		defaultValue: { subject: subjectDefault },
		onValidate({ formData }) {
			const result = parseWithZod(formData, { schema: ContactSchema })
			if (result.status === 'error') {
				for (const [field, errors] of Object.entries(result.error ?? {})) {
					if (errors && errors.length > 0) {
						trackEvent('contact-form', 'validation-error', field)
					}
				}
			}
			return result
		},
		shouldRevalidate: 'onBlur',
	})

	// Track form submission: isPending becomes true when form submits to server.
	// On a coaching arrival, tag the event with the origin for the funnel.
	useEffect(() => {
		if (isPending) {
			if (isCoaching) {
				trackEvent('contact-form', 'submit-success', COACHING_ORIGIN)
			} else {
				trackEvent('contact-form', 'submit-success')
			}
		}
	}, [isPending, isCoaching])

	// Track server errors via actionData
	useEffect(() => {
		if (actionData?.result?.status === 'error') {
			trackEvent('contact-form', 'submit-error')
		}
	}, [actionData])

	const localePath = useLocalizedPath()

	return (
		<>
			{/* Hero */}
			<section className="relative overflow-hidden pt-32 pb-24">
				<HeroBackdrop />

				<div className="relative z-10 container text-center">
					<h1 className="text-h2 md:text-h1 mx-auto max-w-3xl font-bold">
						<span className="text-gradient-brand">{t('contact.heading')}</span>
					</h1>
					<p className="text-body-md text-muted-foreground md:text-body-lg mx-auto mt-6 max-w-2xl">
						{t('contact.subheading')}
					</p>
				</div>
			</section>

			<div className="container pt-12 pb-24">
				{/* Organisation note: the hosting offers live on /cloudron and /nextcloud */}
				<section aria-labelledby="organisation-heading" className="mb-12">
					<Alert tone="info" role="note" className="gap-0 p-8">
						<h2 id="organisation-heading" className="text-h4 mb-3">
							{t('contact.organisationHeading')}
						</h2>
						<p className="text-body-md text-muted-foreground mb-4">
							{t('contact.organisationDescription')}
						</p>
						<div className="flex flex-col items-start gap-2">
							{organisationLinks.map(({ to, labelKey }) => (
								<Button
									key={to}
									variant="link"
									className="text-body-md h-auto px-0"
									asChild
								>
									<Link to={localePath(to)}>
										<Icon name="arrow-right" />
										{t(labelKey)}
									</Link>
								</Button>
							))}
						</div>
					</Alert>
				</section>

				<div className="grid grid-cols-1 gap-12 lg:grid-cols-5">
					<div className="lg:col-span-3">
						<Card className="gap-0 p-6">
							<Form
								method="POST"
								{...getFormProps(form)}
								onFocus={() => {
									if (!formStartedRef.current) {
										formStartedRef.current = true
										if (isCoaching) {
											trackEvent('contact-form', 'start', COACHING_ORIGIN)
										} else {
											trackEvent('contact-form', 'start')
										}
									}
								}}
							>
								<HoneypotInputs />
								<Field
									labelProps={{
										htmlFor: fields.name.id,
										children: t('contact.nameLabel'),
									}}
									inputProps={{
										...getInputProps(fields.name, {
											type: 'text',
										}),
										autoComplete: 'name',
										placeholder: t('contact.namePlaceholder'),
									}}
									errors={fields.name.errors}
								/>
								<Field
									labelProps={{
										htmlFor: fields.email.id,
										children: t('contact.emailLabel'),
									}}
									inputProps={{
										...getInputProps(fields.email, {
											type: 'email',
										}),
										autoComplete: 'email',
										placeholder: t('contact.emailPlaceholder'),
									}}
									errors={fields.email.errors}
								/>
								<Field
									labelProps={{
										htmlFor: fields.subject.id,
										children: t('contact.subjectLabel'),
									}}
									inputProps={{
										...getInputProps(fields.subject, {
											type: 'text',
										}),
										placeholder: t('contact.subjectPlaceholder'),
										className: subjectDefault
											? 'border-primary/40 bg-primary/5'
											: undefined,
									}}
									errors={fields.subject.errors}
								/>
								<TextareaField
									labelProps={{
										htmlFor: fields.message.id,
										children: t('contact.messageLabel'),
									}}
									textareaProps={{
										...getTextareaProps(fields.message),
										placeholder: t(
											isAnliegen(origin)
												? ANLIEGEN[origin].placeholder
												: 'contact.messagePlaceholder',
										),
										rows: 6,
									}}
									errors={fields.message.errors}
								/>
								<ErrorList errors={form.errors} id={form.errorId} />
								<StatusButton
									type="submit"
									status={isPending ? 'pending' : (form.status ?? 'idle')}
									disabled={isPending}
								>
									{t('contact.submit')}
								</StatusButton>
							</Form>
						</Card>
					</div>

					<div className="space-y-8 lg:col-span-2">
						<div>
							<h2 className="text-h4 mb-6">{t('contact.infoHeading')}</h2>
							<div className="text-muted-foreground space-y-4">
								<p className="flex items-center gap-2">
									<Icon name="envelope-closed" />
									info@opensourced.ch
								</p>
								<p>Open Sourced GmbH</p>
								<p>{t('contact.location')}</p>
							</div>
						</div>

						{/* Support note */}
						<div className="border-border border-t pt-6">
							<h3 className="text-body-md mb-2 font-semibold">
								{t('contact.supportHeading')}
							</h3>
							<p className="text-body-sm text-muted-foreground">
								{t('contact.supportDescription')}{' '}
								<a
									href={`mailto:${t('contact.supportEmail')}`}
									className="link-inline focus-cosy"
								>
									{t('contact.supportEmail')}
								</a>
							</p>
						</div>
					</div>
				</div>
			</div>
		</>
	)
}
